/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useCallback, useMemo, useRef, useState } from "react";

/* ─────────────────────────────────────────────────────────────────────────────
 * Global background-job manager.
 *
 * Generation tasks (notes, flashcards, exam papers, reviews, syllabus, …) used
 * to live inside page components. Navigating away unmounted the page, so the
 * in-flight request's result was thrown away and the UI "reset".
 *
 * JobsProvider owns the async lifecycle itself — it never unmounts — so a task
 * keeps running regardless of which page is visible. Pages start a job with
 * `startJob()` and read its live status/result with `getJob()`. On completion a
 * toast fires; every running job gets a Cancel button in the floating tray.
 * ───────────────────────────────────────────────────────────────────────────── */

const JobsContext = createContext(null);

export function useJobs() {
  const ctx = useContext(JobsContext);
  if (!ctx) throw new Error("useJobs must be used inside <JobsProvider>");
  return ctx;
}

let _seq = 0;
const nextId = () => `job_${Date.now().toString(36)}_${_seq++}`;

export function JobsProvider({ children }) {
  const [jobs, _setJobs] = useState([]);   // running + last-finished, keyed by kind+scopeId
  const jobsRef = useRef(jobs);             // synchronous mirror for guards / getJob
  const controllers = useRef({});           // jobId → AbortController
  const [notifs, setNotifs] = useState([]); // transient completion toasts

  const setJobs = useCallback((updater) => {
    _setJobs(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      jobsRef.current = next;
      return next;
    });
  }, []);

  const pushNotif = useCallback((n) => {
    const id = nextId();
    setNotifs(prev => [...prev, { id, ...n }]);
    setTimeout(() => setNotifs(prev => prev.filter(x => x.id !== id)), 4600);
  }, []);

  const dismissNotif = useCallback((id) => {
    setNotifs(prev => prev.filter(x => x.id !== id));
  }, []);

  const startJob = useCallback(({ kind, scopeId = "", label, doneLabel, meta = null, run, onDone, onError }) => {
    // Never run two of the same (kind, scope) at once.
    const running = jobsRef.current.find(
      j => j.kind === kind && j.scopeId === scopeId && j.status === "running"
    );
    if (running) return running.id;

    const id = nextId();
    const controller = new AbortController();
    controllers.current[id] = controller;

    const job = {
      id, kind, scopeId,
      label: label || "Working",
      doneLabel: doneLabel || label || "Task",
      meta,
      status: "running",
      result: null,
      error: null,
      startedAt: Date.now(),
    };

    // Drop any prior finished job for this key, then add the new running one.
    setJobs(prev => [
      ...prev.filter(j => !(j.kind === kind && j.scopeId === scopeId)),
      job,
    ]);

    (async () => {
      try {
        const result = await run(controller.signal);
        if (controller.signal.aborted) return; // handled by cancelJob
        setJobs(prev => prev.map(j => (j.id === id ? { ...j, status: "done", result } : j)));
        pushNotif({ tone: "success", icon: "check_circle", title: `${job.doneLabel} generated` });
        onDone?.(result);
      } catch (err) {
        if (controller.signal.aborted || err?.name === "AbortError") {
          setJobs(prev => prev.map(j => (j.id === id ? { ...j, status: "cancelled" } : j)));
          return;
        }
        const msg = err?.message || "Something went wrong";
        setJobs(prev => prev.map(j => (j.id === id ? { ...j, status: "error", error: msg } : j)));
        pushNotif({ tone: "error", icon: "error", title: `${job.label} failed`, desc: msg });
        onError?.(msg);
      } finally {
        delete controllers.current[id];
      }
    })();

    return id;
  }, [setJobs, pushNotif]);

  const cancelJob = useCallback((id) => {
    const controller = controllers.current[id];
    if (controller) controller.abort();
    setJobs(prev => prev.map(j => (j.id === id ? { ...j, status: "cancelled" } : j)));
    const job = jobsRef.current.find(j => j.id === id);
    if (job) pushNotif({ tone: "neutral", icon: "cancel", title: `${job.label} cancelled` });
  }, [setJobs, pushNotif]);

  const getJob = useCallback(
    (kind, scopeId = "") => jobs.find(j => j.kind === kind && j.scopeId === scopeId),
    [jobs]
  );

  // Forget a job entirely (aborting it first if still running).
  const clearJob = useCallback((kind, scopeId = "") => {
    setJobs(prev => prev.filter(j => {
      const match = j.kind === kind && j.scopeId === scopeId;
      if (match && j.status === "running") controllers.current[j.id]?.abort();
      return !match;
    }));
  }, [setJobs]);

  const runningJobs = useMemo(() => jobs.filter(j => j.status === "running"), [jobs]);

  // Fire a standalone completion/info toast (for flows that manage their own async,
  // e.g. YouTube's backend polling).
  const notify = useCallback((opts) => pushNotif(opts), [pushNotif]);

  const value = useMemo(
    () => ({ jobs, startJob, cancelJob, clearJob, getJob, runningJobs, notify }),
    [jobs, startJob, cancelJob, clearJob, getJob, runningJobs, notify]
  );

  return (
    <JobsContext.Provider value={value}>
      {children}
      <JobsTray jobs={runningJobs} onCancel={cancelJob} />
      <JobNotifications notifs={notifs} onDismiss={dismissNotif} />
    </JobsContext.Provider>
  );
}

/* ── Floating tray of active jobs (with Cancel) ─────────────────────────────── */
function JobsTray({ jobs, onCancel }) {
  if (!jobs.length) return null;
  return (
    <div style={{
      position: "fixed", left: 16, bottom: 16, zIndex: 9000,
      display: "flex", flexDirection: "column", gap: 8, maxWidth: 320,
    }}>
      {jobs.map(job => (
        <div key={job.id} className="animate-fadeIn" style={{
          display: "flex", alignItems: "center", gap: 10,
          background: "var(--surface)", border: "1px solid var(--border-2)",
          borderRadius: "var(--radius-lg)", padding: "10px 12px",
          boxShadow: "var(--shadow-lg)",
        }}>
          <div className="spinner spinner-sm" style={{ borderTopColor: "var(--accent)", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {job.label}
            </div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 1 }}>Running in background…</div>
          </div>
          <button
            onClick={() => onCancel(job.id)}
            title="Cancel"
            style={{
              display: "flex", alignItems: "center", gap: 4, flexShrink: 0,
              padding: "4px 9px", borderRadius: "var(--radius-full)",
              border: "1px solid rgba(244,63,94,0.3)", background: "var(--rose-soft, rgba(244,63,94,0.1))",
              color: "var(--rose)", fontSize: 11, fontWeight: 600, cursor: "pointer",
              transition: "all var(--transition)",
            }}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 13 }}>stop_circle</span>
            Cancel
          </button>
        </div>
      ))}
    </div>
  );
}

/* ── Completion toast stack ─────────────────────────────────────────────────── */
function JobNotifications({ notifs, onDismiss }) {
  if (!notifs.length) return null;
  const toneColor = { success: "var(--green)", error: "var(--rose)", neutral: "var(--ink-3)" };
  return (
    <div style={{
      position: "fixed", right: 16, bottom: 16, zIndex: 9100,
      display: "flex", flexDirection: "column", gap: 8, maxWidth: 340,
    }}>
      {notifs.map(n => {
        const color = toneColor[n.tone] || "var(--accent-2)";
        return (
          <div key={n.id} className="animate-fadeIn" onClick={() => onDismiss(n.id)} style={{
            display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer",
            background: "var(--surface)", border: "1px solid var(--border-2)",
            borderLeft: `3px solid ${color}`,
            borderRadius: "var(--radius-lg)", padding: "11px 13px",
            boxShadow: "var(--shadow-lg)",
          }}>
            <span className="material-symbols-rounded" style={{ fontSize: 18, color, flexShrink: 0, fontVariationSettings: "'FILL' 1" }}>
              {n.icon || "info"}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink)" }}>{n.title}</div>
              {n.desc && <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 2, lineHeight: 1.4 }}>{n.desc}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
